import { supabase } from './supabase';

export const SIGNUP_SELECTION = {
  FIRST_COME: 'first_come',
  HOST_SELECT: 'host_select',
};

export function normalizeSignupSelection(value) {
  return value === SIGNUP_SELECTION.HOST_SELECT
    ? SIGNUP_SELECTION.HOST_SELECT
    : SIGNUP_SELECTION.FIRST_COME;
}

export function isHostSelectSignup(sessionOrValue) {
  const value = typeof sessionOrValue === 'string' || sessionOrValue == null
    ? sessionOrValue
    : sessionOrValue.signup_selection;
  return normalizeSignupSelection(value) === SIGNUP_SELECTION.HOST_SELECT;
}

/**
 * Placement for a new pre-start signup row.
 * @returns {{ signupStatus: 'confirmed'|'waitlist'|null, waitlistPosition: number|null, blocked?: boolean, blockedReason?: string }}
 */
export function initialSignupPlacement(session, {
  confirmedCount = 0,
  waitlistCount = 0,
  maxPlayers,
} = {}) {
  if (isHostSelectSignup(session)) {
    return {
      signupStatus: 'waitlist',
      waitlistPosition: (waitlistCount || 0) + 1,
    };
  }

  const max = maxPlayers ?? session?.max_players ?? 16;
  if (confirmedCount < max) {
    return { signupStatus: 'confirmed', waitlistPosition: null };
  }
  if (session?.waitlist_enabled === false) {
    return {
      signupStatus: null,
      waitlistPosition: null,
      blocked: true,
      blockedReason: 'Session is full and waitlist is disabled',
    };
  }
  return {
    signupStatus: 'waitlist',
    waitlistPosition: (waitlistCount || 0) + 1,
  };
}

export function canSignupByCapacity(session, confirmedCount = 0) {
  if (isHostSelectSignup(session)) return true;
  const max = session?.max_players || 16;
  return confirmedCount < max || session?.waitlist_enabled !== false;
}

export function getWaitlistCountFromSession(session) {
  if (!session) return null;
  if (Array.isArray(session.waitlist_players)) return session.waitlist_players.length;
  if (typeof session.waitlist_players === 'number') return session.waitlist_players;
  if (typeof session.waitlistCount === 'number') return session.waitlistCount;
  return null;
}

export function getHostSelectAvailabilityLabel({ maxPlayers, waitlistCount } = {}) {
  const max = maxPlayers || 16;
  const onList = waitlistCount || 0;
  return `Up to ${max} players · ${onList} on the list`;
}

export function getHostSelectCapacitySummary({ confirmedCount = 0, maxPlayers = 16 } = {}) {
  const max = maxPlayers || 16;
  const confirmed = confirmedCount || 0;
  const base = `${confirmed} confirmed · ${max} shown to players`;
  if (confirmed > max) return `${base} · above the number shown to players`;
  return base;
}

export function getHostSelectStartWarning(waitlistCount) {
  const count = Number(waitlistCount) || 0;
  if (count <= 0) return null;
  const noun = count === 1 ? 'player' : 'players';
  const verb = count === 1 ? 'is' : 'are';
  return `${count} ${noun} ${verb} still on the list. Starting will only add confirmed players.`;
}

export async function fetchHostSelectStartWarning(session) {
  if (!isHostSelectSignup(session)) return null;
  let count = getWaitlistCountFromSession(session);
  if (count == null && session?.id) {
    const { count: fetched, error } = await supabase
      .from('session_signups')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', session.id)
      .eq('signup_status', 'waitlist');
    if (error) {
      console.warn('fetchHostSelectStartWarning waitlist count:', error);
      count = 0;
    } else {
      count = fetched || 0;
    }
  }
  return getHostSelectStartWarning(count);
}
